import { AgentAPIRequest, AgentAPIResponse } from './AgentAPIGateway';

export interface Lead {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
  company?: string;
  title?: string;
  source: string;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  leadScore: number;
  tags: string[];
  location?: string;
  notes?: string;
  customFields?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
  lastEngaged?: Date;
  assignedTo?: string;
  segmentIds?: string[];
}

export interface LeadSearchParams {
  query?: string;
  status?: string;
  source?: string;
  minScore?: number;
  maxScore?: number;
  tags?: string[];
  assignedTo?: string;
  segmentId?: string;
  createdAfter?: Date;
  createdBefore?: Date;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'email' | 'leadScore' | 'createdAt' | 'lastEngaged';
  sortOrder?: 'asc' | 'desc';
}

export interface LeadSearchResponse {
  leads: Lead[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface BulkLeadOperation {
  operation: 'create' | 'update' | 'delete';
  leads: Partial<Lead>[];
}

export interface BulkLeadResponse {
  successful: number;
  failed: number;
  errors: Array<{
    index: number;
    error: string;
    leadData?: Partial<Lead>;
  }>;
  results: Lead[];
}

export interface LeadPrivacySettings {
  allowedFields: string[];
  restrictedFields: string[];
  requireConsent: boolean;
  dataRetentionDays?: number;
}

export class AgentLeadService {
  private mockLeads: Lead[] = [
    {
      id: 'lead_1',
      email: 'john.doe@example.com',
      firstName: 'John',
      lastName: 'Doe',
      name: 'John Doe',
      phone: '+1-555-0123',
      company: 'Acme Corp',
      title: 'Marketing Director',
      source: 'Website',
      status: 'qualified',
      leadScore: 85,
      tags: ['enterprise', 'marketing'],
      location: 'New York, NY',
      notes: 'Interested in enterprise solution',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-20'),
      lastEngaged: new Date('2024-01-20'),
      assignedTo: 'agent_1',
      segmentIds: ['segment_1']
    },
    {
      id: 'lead_2',
      email: 'jane.smith@techstart.com',
      firstName: 'Jane',
      lastName: 'Smith',
      name: 'Jane Smith',
      phone: '+1-555-0456',
      company: 'TechStart Inc',
      title: 'CEO',
      source: 'LinkedIn',
      status: 'new',
      leadScore: 92,
      tags: ['startup', 'ceo'],
      location: 'San Francisco, CA',
      notes: 'Startup founder, high potential',
      createdAt: new Date('2024-01-18'),
      updatedAt: new Date('2024-01-18'),
      lastEngaged: new Date('2024-01-18'),
      assignedTo: 'agent_2',
      segmentIds: ['segment_2']
    },
    {
      id: 'lead_3',
      email: 'bob.wilson@smallbiz.com',
      firstName: 'Bob',
      lastName: 'Wilson',
      name: 'Bob Wilson',
      phone: '+1-555-0789',
      company: 'Small Biz LLC',
      title: 'Owner',
      source: 'Referral',
      status: 'contacted',
      leadScore: 65,
      tags: ['small-business'],
      location: 'Austin, TX',
      notes: 'Small business owner, price sensitive',
      createdAt: new Date('2024-01-10'),
      updatedAt: new Date('2024-01-16'),
      lastEngaged: new Date('2024-01-16'),
      assignedTo: 'agent_1',
      segmentIds: []
    }
  ];

  private privacySettings: LeadPrivacySettings = {
    allowedFields: [
      'id', 'email', 'firstName', 'lastName', 'name', 'company', 'title',
      'source', 'status', 'leadScore', 'tags', 'location', 'createdAt',
      'updatedAt', 'lastEngaged', 'segmentIds'
    ],
    restrictedFields: ['phone', 'notes', 'customFields'],
    requireConsent: true,
    dataRetentionDays: 365
  };

  /**
   * Create a new lead
   */
  async createLead(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const leadData = request.body as Partial<Lead>;

      // Validate required fields
      if (!leadData.email) {
        return {
          status: 400,
          headers: {},
          data: null,
          duration: 10,
          requestId: request.requestId,
          success: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Email is required',
            details: { field: 'email' }
          },
          metadata: {
            requestId: request.requestId,
            timestamp: new Date(),
            processingTime: 10,
            rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
          }
        };
      }

      // Check for duplicate email
      const existingLead = this.mockLeads.find(lead => lead.email === leadData.email);
      if (existingLead) {
        return {
          status: 409,
          headers: {},
          data: null,
          duration: 15,
          requestId: request.requestId,
          success: false,
          error: {
            code: 'DUPLICATE_EMAIL',
            message: 'Lead with this email already exists',
            details: { existingLeadId: existingLead.id }
          },
          metadata: {
            requestId: request.requestId,
            timestamp: new Date(),
            processingTime: 15,
            rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
          }
        };
      }

      // Apply privacy filtering
      const filteredData = this.applyPrivacyFiltering(leadData, 'create');

      // Create new lead
      const newLead: Lead = {
        id: `lead_${Date.now()}`,
        email: filteredData.email!,
        firstName: filteredData.firstName,
        lastName: filteredData.lastName,
        name: filteredData.name || `${filteredData.firstName || ''} ${filteredData.lastName || ''}`.trim(),
        phone: filteredData.phone,
        company: filteredData.company,
        title: filteredData.title,
        source: filteredData.source || 'API',
        status: filteredData.status || 'new',
        leadScore: filteredData.leadScore || 0,
        tags: filteredData.tags || [],
        location: filteredData.location,
        notes: filteredData.notes,
        customFields: filteredData.customFields,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastEngaged: new Date(),
        assignedTo: filteredData.assignedTo,
        segmentIds: filteredData.segmentIds || []
      };

      this.mockLeads.push(newLead);

      return {
        status: 201,
        headers: {},
        data: this.applyPrivacyFiltering(newLead, 'read'),
        duration: 120,
        requestId: request.requestId,
        success: true,
        metadata: {
          requestId: request.requestId,
          timestamp: new Date(),
          processingTime: 120,
          rateLimit: { remaining: 98, resetTime: new Date(Date.now() + 3600000) }
        }
      };
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  /**
   * Get lead by ID
   */
  async getLeadById(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const leadId = request.parameters.id;
      const lead = this.mockLeads.find(l => l.id === leadId);

      if (!lead) {
        return {
          status: 404,
          headers: {},
          data: null,
          duration: 25,
          requestId: request.requestId,
          success: false,
          error: {
            code: 'LEAD_NOT_FOUND',
            message: 'Lead not found',
            details: { leadId }
          },
          metadata: {
            requestId: request.requestId,
            timestamp: new Date(),
            processingTime: 25,
            rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
          }
        };
      }

      return {
        status: 200,
        headers: {},
        data: this.applyPrivacyFiltering(lead, 'read'),
        duration: 45,
        requestId: request.requestId,
        success: true,
        metadata: {
          requestId: request.requestId,
          timestamp: new Date(),
          processingTime: 45,
          rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
        }
      };
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  /**
   * Update lead
   */
  async updateLead(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const leadId = request.parameters.id;
      const updateData = request.body as Partial<Lead>;
      const leadIndex = this.mockLeads.findIndex(l => l.id === leadId);

      if (leadIndex === -1) {
        return {
          status: 404,
          headers: {},
          data: null,
          duration: 30,
          requestId: request.requestId,
          success: false,
          error: {
            code: 'LEAD_NOT_FOUND',
            message: 'Lead not found',
            details: { leadId }
          },
          metadata: {
            requestId: request.requestId,
            timestamp: new Date(),
            processingTime: 30,
            rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
          }
        };
      }

      // Apply privacy filtering
      const filteredData = this.applyPrivacyFiltering(updateData, 'update');

      // Update lead
      const existingLead = this.mockLeads[leadIndex];
      const updatedLead: Lead = {
        ...existingLead,
        ...filteredData,
        updatedAt: new Date(),
        // Preserve certain fields that shouldn't be overwritten
        id: existingLead.id,
        createdAt: existingLead.createdAt
      };

      this.mockLeads[leadIndex] = updatedLead;

      return {
        status: 200,
        headers: {},
        data: this.applyPrivacyFiltering(updatedLead, 'read'),
        duration: 85,
        requestId: request.requestId,
        success: true,
        metadata: {
          requestId: request.requestId,
          timestamp: new Date(),
          processingTime: 85,
          rateLimit: { remaining: 98, resetTime: new Date(Date.now() + 3600000) }
        }
      };
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  /**
   * Delete lead (soft delete)
   */
  async deleteLead(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const leadId = request.parameters.id;
      const leadIndex = this.mockLeads.findIndex(l => l.id === leadId);

      if (leadIndex === -1) {
        return {
          status: 404,
          headers: {},
          data: null,
          duration: 25,
          requestId: request.requestId,
          success: false,
          error: {
            code: 'LEAD_NOT_FOUND',
            message: 'Lead not found',
            details: { leadId }
          },
          metadata: {
            requestId: request.requestId,
            timestamp: new Date(),
            processingTime: 25,
            rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
          }
        };
      }

      // In a real implementation, this would be a soft delete
      // For now, we'll actually remove it from the mock array
      this.mockLeads.splice(leadIndex, 1);

      return {
        status: 200,
        headers: {},
        data: { deleted: true, leadId },
        duration: 60,
        requestId: request.requestId,
        success: true,
        metadata: {
          requestId: request.requestId,
          timestamp: new Date(),
          processingTime: 60,
          rateLimit: { remaining: 98, resetTime: new Date(Date.now() + 3600000) }
        }
      };
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  /**
   * Search leads with filtering and pagination
   */
  async searchLeads(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const params = request.parameters as LeadSearchParams;
      let filteredLeads = [...this.mockLeads];

      // Apply search filters
      if (params.query) {
        const query = params.query.toLowerCase();
        filteredLeads = filteredLeads.filter(lead =>
          lead.name?.toLowerCase().includes(query) ||
          lead.email.toLowerCase().includes(query) ||
          lead.company?.toLowerCase().includes(query) ||
          lead.tags.some(tag => tag.toLowerCase().includes(query))
        );
      }

      if (params.status) {
        filteredLeads = filteredLeads.filter(lead => lead.status === params.status);
      }

      if (params.source) {
        filteredLeads = filteredLeads.filter(lead => lead.source === params.source);
      }

      if (params.minScore !== undefined) {
        filteredLeads = filteredLeads.filter(lead => lead.leadScore >= params.minScore!);
      }

      if (params.maxScore !== undefined) {
        filteredLeads = filteredLeads.filter(lead => lead.leadScore <= params.maxScore!);
      }

      if (params.tags && params.tags.length > 0) {
        filteredLeads = filteredLeads.filter(lead =>
          params.tags!.some(tag => lead.tags.includes(tag))
        );
      }

      if (params.assignedTo) {
        filteredLeads = filteredLeads.filter(lead => lead.assignedTo === params.assignedTo);
      }

      if (params.segmentId) {
        filteredLeads = filteredLeads.filter(lead =>
          lead.segmentIds?.includes(params.segmentId!)
        );
      }

      if (params.createdAfter) {
        filteredLeads = filteredLeads.filter(lead =>
          lead.createdAt >= params.createdAfter!
        );
      }

      if (params.createdBefore) {
        filteredLeads = filteredLeads.filter(lead =>
          lead.createdAt <= params.createdBefore!
        );
      }

      // Apply sorting
      const sortBy = params.sortBy || 'createdAt';
      const sortOrder = params.sortOrder || 'desc';

      filteredLeads.sort((a, b) => {
        let aValue: any, bValue: any;

        switch (sortBy) {
          case 'name':
            aValue = a.name?.toLowerCase() || '';
            bValue = b.name?.toLowerCase() || '';
            break;
          case 'email':
            aValue = a.email.toLowerCase();
            bValue = b.email.toLowerCase();
            break;
          case 'leadScore':
            aValue = a.leadScore;
            bValue = b.leadScore;
            break;
          case 'lastEngaged':
            aValue = a.lastEngaged || a.createdAt;
            bValue = b.lastEngaged || b.createdAt;
            break;
          case 'createdAt':
          default:
            aValue = a.createdAt;
            bValue = b.createdAt;
            break;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });

      // Apply pagination
      const page = params.page || 1;
      const limit = Math.min(params.limit || 50, 100); // Max 100 per page
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

      // Apply privacy filtering to results
      const sanitizedLeads = paginatedLeads.map(lead => this.applyPrivacyFiltering(lead, 'read'));

      const response: LeadSearchResponse = {
        leads: sanitizedLeads,
        total: filteredLeads.length,
        page,
        limit,
        totalPages: Math.ceil(filteredLeads.length / limit),
        hasNext: endIndex < filteredLeads.length,
        hasPrev: page > 1
      };

      return {
        status: 200,
        headers: {},
        data: response,
        duration: 150,
        requestId: request.requestId,
        success: true,
        metadata: {
          requestId: request.requestId,
          timestamp: new Date(),
          processingTime: 150,
          rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
        }
      };
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  /**
   * Bulk lead operations
   */
  async bulkLeadOperations(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const bulkOperation = request.body as BulkLeadOperation;
      const results: Lead[] = [];
      const errors: Array<{ index: number; error: string; leadData?: Partial<Lead> }> = [];
      let successful = 0;
      let failed = 0;

      for (let i = 0; i < bulkOperation.leads.length; i++) {
        const leadData = bulkOperation.leads[i];

        try {
          switch (bulkOperation.operation) {
            case 'create':
              if (!leadData.email) {
                errors.push({
                  index: i,
                  error: 'Email is required',
                  leadData
                });
                failed++;
                continue;
              }

              // Check for duplicate
              const existingLead = this.mockLeads.find(lead => lead.email === leadData.email);
              if (existingLead) {
                errors.push({
                  index: i,
                  error: 'Lead with this email already exists',
                  leadData
                });
                failed++;
                continue;
              }

              const newLead: Lead = {
                id: `lead_${Date.now()}_${i}`,
                email: leadData.email!,
                firstName: leadData.firstName,
                lastName: leadData.lastName,
                name: leadData.name || `${leadData.firstName || ''} ${leadData.lastName || ''}`.trim(),
                phone: leadData.phone,
                company: leadData.company,
                title: leadData.title,
                source: leadData.source || 'Bulk Import',
                status: leadData.status || 'new',
                leadScore: leadData.leadScore || 0,
                tags: leadData.tags || [],
                location: leadData.location,
                notes: leadData.notes,
                customFields: leadData.customFields,
                createdAt: new Date(),
                updatedAt: new Date(),
                lastEngaged: new Date(),
                assignedTo: leadData.assignedTo,
                segmentIds: leadData.segmentIds || []
              };

              this.mockLeads.push(newLead);
              results.push(this.applyPrivacyFiltering(newLead, 'read'));
              successful++;
              break;

            case 'update':
              if (!leadData.id) {
                errors.push({
                  index: i,
                  error: 'Lead ID is required for update',
                  leadData
                });
                failed++;
                continue;
              }

              const leadIndex = this.mockLeads.findIndex(l => l.id === leadData.id);
              if (leadIndex === -1) {
                errors.push({
                  index: i,
                  error: 'Lead not found',
                  leadData
                });
                failed++;
                continue;
              }

              const existingLeadForUpdate = this.mockLeads[leadIndex];
              const updatedLead: Lead = {
                ...existingLeadForUpdate,
                ...this.applyPrivacyFiltering(leadData, 'update'),
                updatedAt: new Date(),
                id: existingLeadForUpdate.id,
                createdAt: existingLeadForUpdate.createdAt
              };

              this.mockLeads[leadIndex] = updatedLead;
              results.push(this.applyPrivacyFiltering(updatedLead, 'read'));
              successful++;
              break;

            case 'delete':
              if (!leadData.id) {
                errors.push({
                  index: i,
                  error: 'Lead ID is required for delete',
                  leadData
                });
                failed++;
                continue;
              }

              const deleteIndex = this.mockLeads.findIndex(l => l.id === leadData.id);
              if (deleteIndex === -1) {
                errors.push({
                  index: i,
                  error: 'Lead not found',
                  leadData
                });
                failed++;
                continue;
              }

              this.mockLeads.splice(deleteIndex, 1);
              successful++;
              break;

            default:
              errors.push({
                index: i,
                error: 'Invalid operation',
                leadData
              });
              failed++;
          }
        } catch (error) {
          errors.push({
            index: i,
            error: error instanceof Error ? error.message : 'Unknown error',
            leadData
          });
          failed++;
        }
      }

      const response: BulkLeadResponse = {
        successful,
        failed,
        errors,
        results
      };

      return {
        status: 200,
        headers: {},
        data: response,
        duration: bulkOperation.leads.length * 50,
        requestId: request.requestId,
        success: true,
        metadata: {
          requestId: request.requestId,
          timestamp: new Date(),
          processingTime: bulkOperation.leads.length * 50,
          rateLimit: { remaining: 95, resetTime: new Date(Date.now() + 3600000) }
        }
      };
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  /**
   * Apply privacy filtering based on agent permissions
   */
  private applyPrivacyFiltering(data: Partial<Lead>, operation: 'create' | 'read' | 'update'): any {
    const filtered: any = {};

    for (const [key, value] of Object.entries(data)) {
      if (this.privacySettings.allowedFields.includes(key)) {
        filtered[key] = value;
      } else if (this.privacySettings.restrictedFields.includes(key)) {
        // For read operations, exclude restricted fields
        if (operation !== 'read') {
          filtered[key] = value;
        }
      } else {
        // Allow other fields by default
        filtered[key] = value;
      }
    }

    return filtered;
  }

  /**
   * Handle service errors
   */
  private handleError(error: any, requestId: string): AgentAPIResponse {
    console.error('AgentLeadService error:', error);

    return {
      status: 500,
      headers: {},
      data: null,
      duration: 50,
      requestId,
      success: false,
      error: {
        code: 'INTERNAL_ERROR',
        message: 'An internal error occurred while processing the lead request',
        details: process.env.NODE_ENV === 'development' ? error.message : undefined
      },
      metadata: {
        requestId,
        timestamp: new Date(),
        processingTime: 50,
        rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
      }
    };
  }

  /**
   * Get privacy settings for the agent
   */
  getPrivacySettings(): LeadPrivacySettings {
    return { ...this.privacySettings };
  }

  /**
   * Update privacy settings (admin only)
   */
  updatePrivacySettings(settings: Partial<LeadPrivacySettings>): void {
    this.privacySettings = { ...this.privacySettings, ...settings };
  }
}